import { useState, useContext, useEffect, useRef, useMemo, useCallback } from 'react';
import classNames from 'classnames';
import T from '../../../../common/components/T';
import Skeleton from '../../../../widgets/plug/Skeleton/Skeleton';
import css from './PartnerStats.module.scss';
import { partnersSkeleton } from './helpers';
import UserContext from '../../../../common/utils/getContext';
import { getData } from '../../../../common/utils/router';
import CardWithIcon from '../../../../widgets/cardWithIcon/CardWithIcon';
import { svgSprite } from '../../../../hybrid/wrappers';
import { useDraggable } from '../../../../common/utils/hooks';
import { checkIsArray } from '../../../../common/utils/utils';

const CardInfo = ({ data }) => {
    const isStructure = useMemo(() => data?.structure, [data]);
    const [growth, setGrowth] = useState({});

    useEffect(() => {
        if (data?.structure && checkIsArray(data?.partners) && checkIsArray(data?.comparison_partners)) {
            const growthObj = {};
            checkIsArray(data.partners) &&
                data.partners.map((item) => {
                    data.comparison_partners.forEach((comparison_item) => {
                        if (item.position === comparison_item.position) {
                            growthObj[item.position] = item.count - comparison_item.count;
                        }
                    });
                });
            setGrowth(growthObj);
        }
    }, [data]);

    const growth_first_ref = useRef();
    const before_ref = useRef();

    // вирівнювання для нижнього значення (для дизайну)
    useEffect(() => {
        if (growth_first_ref.current) {
            const dom = growth_first_ref.current.getBoundingClientRect();

            if (before_ref.current) {
                const beforeWidth = before_ref.current.getBoundingClientRect().width;
                if (beforeWidth > dom.width) {
                    growth_first_ref.current.style.width = beforeWidth + 12 + 'px';
                } else {
                    before_ref.current.style.width = dom.width + 'px';
                }
            }
        }
    }, [growth_first_ref.current, before_ref]);

    return (
        <div className={css.partnersLineWrapper}>
            <div className={css.partnersLine}>
                <p className={css.partnersLabel}>
                    <T textName='increase' defaultStr='Прирост' page='/office' />:
                </p>
                {!isStructure ? (
                    <div className={css.partnersGrowth}>
                        {data?.growth ? (
                            <div className={css.partnersGrowthIcon}>
                                {svgSprite('arrow-small', { className: css.partnersGrowthSvg })}
                            </div>
                        ) : null}
                        <p className={css.partnersValue}>{Number(data?.growth || '0').toLocaleString()}</p>
                    </div>
                ) : (
                    <div className={css.partnersGrowthStructure}>
                        <div className={css.partnersGrowth}>
                            {growth?.[1] > 0 ? (
                                <div className={css.partnersGrowthIcon}>
                                    {svgSprite('arrow-small', { className: css.partnersGrowthSvg })}
                                </div>
                            ) : null}
                            <p className={css.partnersValue}>{Number(growth?.[1] || '0').toLocaleString()}</p>
                        </div>
                        <div ref={growth_first_ref} className={classNames(css.partnersGrowth, css.partners2Growth)}>
                            {growth?.[2] > 0 ? (
                                <div className={css.partnersGrowthIcon}>
                                    {svgSprite('arrow-small', { className: css.partnersGrowthSvg })}
                                </div>
                            ) : null}
                            <p className={css.partnersValue}>{Number(growth?.[2] || '0').toLocaleString()}</p>
                        </div>
                    </div>
                )}
            </div>
            <div className={css.partnersLine}>
                <p className={css.partnersLabel}>
                    <T textName='for_the_past' defaultStr='За прошлый' page='/office' />:
                </p>
                <p className={classNames(css.partnersValue)}>
                    {data?.comparison_partners && Array.isArray(data?.comparison_partners)
                        ? Number(data?.comparison_partners?.[0]?.count || '0').toLocaleString()
                        : Number(data?.comparison_number || '0').toLocaleString()}
                    {data?.comparison_partners && Array.isArray(data?.comparison_partners) ? (
                        <span ref={before_ref} className={css.comparisonValue}>
                            {Number(data?.comparison_partners?.[1]?.count || '0').toLocaleString()}
                        </span>
                    ) : null}
                </p>
            </div>
        </div>
    );
};

const RenderData = (props) => {
    const { response: data } = props;
    const blockRef = useRef();
    // хук для скрола
    useDraggable(blockRef);

    const period = useMemo(() => {
        if (data?.data?.comparison_period?.id_formatted) {
            return `${data?.data?.comparison_period?.id_formatted} - ${data?.data?.period?.id_formatted}`;
        }
        return data?.data?.period?.id_formatted;
    }, [data]);

    return (
        <div className={css.block}>
            <div className={css.head}>
                <p className={css.title}>
                    <T textName='partners_stats_title' defaultStr='Статистика по партнерам' page='/office' />
                </p>
                <div className={css.period}>
                    <span className={css.periodLabel}>
                        <T textName='current-period' defaultStr='Текущий период' page='/office' />:
                    </span>
                    <span className={css.periodValue}>{period}</span>
                </div>
            </div>
            <div ref={blockRef} className={classNames(css.grid, 'scrollbar-hidden')}>
                <div className={css.gridItem}>
                    <CardWithIcon
                        name={<T textName='new_partners' defaultStr='Новые партнеры' page='/office' />}
                        formatted_value={Number(data?.data?.stats?.new_partners?.number || '0').toLocaleString()}
                        icon='plus-with-elipse'
                        component={<CardInfo data={data?.data?.stats?.new_partners} />}
                        customCss={{ wrapper: css.card }}
                    />
                </div>
                <div className={css.gridItem}>
                    <CardWithIcon
                        name={<T textName='active_partners' defaultStr='Активные партнеры' page='/office' />}
                        formatted_value={Number(data?.data?.stats?.active_partners?.number || '0').toLocaleString()}
                        icon='star-with-elipse'
                        component={<CardInfo data={data?.data?.stats?.active_partners} />}
                        customCss={{ wrapper: css.card }}
                    />
                </div>
                <div className={css.gridItem}>
                    <CardWithIcon
                        name={<T textName='referrals_partners' defaultStr='Лично приглашенные' page='/office' />}
                        formatted_value={Number(data?.data?.stats?.referrals?.number || '0').toLocaleString()}
                        icon='handshake-with-elipse'
                        component={<CardInfo data={data?.data?.stats?.referrals} />}
                        customCss={{ wrapper: css.card }}
                    />
                </div>
                {/* <div className={css.gridItem}>
                    <CardWithIcon
                        name={<T textName='all_partners' defaultStr='Всего партнеров' page='/office' />}
                        formatted_value={data?.data?.stats?.partners?.number || '0'}
                        icon='users-fill-colored'
                        component={<CardInfo data={data?.data?.stats?.partners} />}
                        customCss={{ wrapper: css.card }}
                    />
                </div> */}
                <div className={css.gridItem}>
                    <CardWithIcon
                        name={<T textName='binar_partners_active' defaultStr='Активных партнеров в бинаре' page='/office' />}
                        formatted_value={Number(
                            data?.data?.stats?.partners_in_structures?.[0]?.partners?.[0]?.count || '0'
                        ).toLocaleString()}
                        comparison_value={Number(
                            data?.data?.stats?.partners_in_structures?.[0]?.partners?.[1]?.count || '0'
                        ).toLocaleString()}
                        icon='branches-with-elipse'
                        component={<CardInfo data={data?.data?.stats?.partners_in_structures?.[0]} />}
                        customCss={{ wrapper: css.card }}
                    />
                </div>
                {/* <div className={css.gridItem}>
                    <CardWithIcon
                        name={<T textName='deleted_partners' defaultStr='Удаленных партнеров' page='/office' />}
                        formatted_value={data?.data?.stats?.delete_partners?.number || '0'}
                        icon='basket-with-elipse'
                        component={<CardInfo data={data?.data?.stats?.delete_partners} />}
                    />
                </div> */}
            </div>
        </div>
    );
};

export default function PartnerStats() {
    const { userToken, lang } = useContext(UserContext);

    const handler = useCallback(
        () => getData('/api/v1/user/marketing/partner-stats-by-periods-comparison', userToken, lang),
        [userToken, lang]
    );

    return <Skeleton lazy={true} handler={handler} Component={RenderData} pattern={partnersSkeleton} />;
}
